import {
	OAUTH_AUTHORIZE_PATH,
	OAUTH_CLIENT_ID_MAX_LENGTH,
	OAUTH_LOGIN_HINT_MAX_LENGTH,
	OAUTH_PARAMETER_MAX_LENGTH,
	SUBDOMAIN_PATTERN,
} from '@/lib/constants';

export type OAuthAuthorizeRequest = {
	response_type: 'code';
	client_id: string;
	redirect_uri: string;
	scope: string;
	state: string;
	code_challenge: string;
	code_challenge_method: 'S256';
	login_hint?: string;
};

export function readOAuthAuthorizeRequest(location: Location): OAuthAuthorizeRequest | undefined {
	if (location.pathname !== OAUTH_AUTHORIZE_PATH) return undefined;

	const parameters = new URLSearchParams(location.search);
	const responseType = parameters.get('response_type');
	const clientId = parameters.get('client_id');
	const redirectUri = parameters.get('redirect_uri');
	const scope = parameters.get('scope');
	const state = parameters.get('state');
	const codeChallenge = parameters.get('code_challenge');
	const codeChallengeMethod = parameters.get('code_challenge_method');

	if (
		responseType !== 'code' ||
		!clientId ||
		!redirectUri ||
		!scope ||
		!state ||
		!codeChallenge ||
		codeChallengeMethod !== 'S256'
	)
		return undefined;

	const loginHint = parameters.get('login_hint');
	// Match Symfony Length's Unicode code-point limits on OAuthAuthorizePayload.
	if (
		Array.from(clientId).length > OAUTH_CLIENT_ID_MAX_LENGTH ||
		[redirectUri, scope, state, codeChallenge].some(
			(value) => Array.from(value).length > OAUTH_PARAMETER_MAX_LENGTH,
		) ||
		(loginHint && Array.from(loginHint).length > OAUTH_LOGIN_HINT_MAX_LENGTH)
	)
		return undefined;

	return {
		response_type: responseType,
		client_id: clientId,
		redirect_uri: redirectUri,
		scope,
		state,
		code_challenge: codeChallenge,
		code_challenge_method: codeChallengeMethod,
		...(loginHint ? { login_hint: loginHint } : {}),
	};
}

export function subdomainFromPlatformUrlHint(location: Location, hosts: string[]): string | undefined {
	const hint = new URLSearchParams(location.search).get('platform_url_hint');
	if (!hint) return undefined;

	try {
		const url = new URL(hint);
		if (url.protocol !== 'https:' || url.username || url.password || url.port) return undefined;

		const hostname = url.hostname.toLowerCase();
		for (const host of hosts) {
			const suffix = `.${host.toLowerCase()}`;
			if (!hostname.endsWith(suffix)) continue;

			const subdomain = hostname.slice(0, -suffix.length);
			return SUBDOMAIN_PATTERN.test(subdomain) ? subdomain : undefined;
		}
	} catch {
		return undefined;
	}
}
